import { ArrowLeftIcon } from '@components/Icons/ArrowLeftIcon';
import { InternalTestingBadge } from '@components/InternalTestingBadge/InternalTestingBadge';
import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { BackTo } from '@components/util/BackToPage';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { PUBLISHING_SETTINGS_TAB } from '@lib/utils/socialConnectReturn';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { AppPage } from '@pages/AppPage';
import { debugStore } from '@stores/Debug';
import { isReachableWhileWalled, paywallStore } from '@stores/Paywall';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiKeys } from './components/ApiKeys/ApiKeys';
import { Billing } from './components/Billing/Billing';
import { ChangeNicknameForm } from './components/ChangeNickname/ChangeNickname';
import { ChangePasswordForm } from './components/ChangePassword/ChangePassword';
import { DebugTab } from './components/Debug/DebugTab';
import { Publishing } from './components/Publishing/Publishing';
import css from './Settings.module.scss';

/**
 * The one list of tabs. The union, the mobile toggle and the desktop toggle
 * all derive from it, so a tab is added here and nowhere else. The Debug tab
 * is not in it: it is unlocked by a click sequence and rendered by hand below.
 */
const SETTINGS_TABS = [
  { value: 'account', label: 'Account' },
  { value: 'billing', label: 'Billing' },
  { value: PUBLISHING_SETTINGS_TAB, label: 'Publishing' },
  { value: 'api-keys', label: 'API Keys' },
] as const satisfies readonly { value: string; label: string }[];

type SettingsTab = (typeof SETTINGS_TABS)[number]['value'] | 'debug';
const DEFAULT_TAB: SettingsTab = 'billing';

const isSettingsTab = (value: string): value is SettingsTab =>
  value === 'debug' || SETTINGS_TABS.some((t) => t.value === value);

export const SettingsPage = observer(function SettingsPage() {
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const tab: SettingsTab = urlTab && isSettingsTab(urlTab) ? urlTab : DEFAULT_TAB;

  useEffect(() => {
    // Legacy alias: the Account tab used to live at /settings/password.
    // Redirect so shared links / bookmarks keep working.
    if (urlTab === 'password') {
      navigate('/settings/account', { replace: true });
      return;
    }
    if (!urlTab || !isSettingsTab(urlTab)) {
      navigate(`/settings/${DEFAULT_TAB}`, { replace: true });
    }
  }, [urlTab, navigate]);

  // Track settings tab views
  useEffect(() => {
    trackingStore.settingsTabViewed(tab);
  }, [tab]);

  const handleDebugClick = () => {
    debugStore.handleDebugClick(() => {
      navigate(`/settings/debug`, { replace: true });
    });
  };

  // The Publishing tab is an entry point to publishing: team-only while it is
  // in internal testing, badged as such (see `FIXME(publishing-internal-only)`).
  // The route still answers for everyone; only the button is withheld.
  // Behind the paywall only the tabs the wall lets through are offered; the
  // gate in WithUser bounces a direct URL to the others.
  const visibleTabs = SETTINGS_TABS.filter(
    (t) =>
      (t.value !== PUBLISHING_SETTINGS_TAB || debugStore.showPublishingEntryPoints) &&
      (!paywallStore.isWalled || isReachableWhileWalled(`/settings/${t.value}`)),
  );
  const renderTabLabel = (t: (typeof SETTINGS_TABS)[number]) => (
    <>
      {t.label}
      {t.value === PUBLISHING_SETTINGS_TAB && debugStore.publishingInternalOnly && (
        <InternalTestingBadge className={css.tabBadge} />
      )}
    </>
  );

  const mobileTabOptions: { value: SettingsTab; label: string; customLabel?: ReactNode }[] =
    visibleTabs.map((t) => ({ ...t, customLabel: renderTabLabel(t) }));
  if (debugStore.respectfulDebugMode) {
    mobileTabOptions.push({ value: 'debug', label: 'Debug' });
  }

  if (isMobile) {
    return (
      <div className={css.mobileSettingsPage}>
        <button className={css.mobileBackButton} onClick={() => navigate('/account')}>
          <ArrowLeftIcon />
        </button>
        <div className={css.mobileToggleContainer}>
          <MultipleToggle
            options={mobileTabOptions}
            value={tab}
            onChange={(value) => navigate(`/settings/${value}`, { replace: true })}
          />
        </div>
        <GradientScrollDiv className={css.mobileContent} innerDivClassName={css.mobileContentInner}>
          {tab === 'account' && (
            <>
              <h2 className={css.mobileHeading}>Nickname</h2>
              <p className={css.mobileSubheading}>How we'll call you around StarZero.</p>
              <ChangeNicknameForm mobile />
              <h2 className={css.mobileHeading}>
                {userStore.data?.hasPassword === false ? 'Set Password' : 'Change Password'}
              </h2>
              <p className={css.mobileSubheading}>
                {userStore.data?.hasPassword === false
                  ? 'Create a password to also sign in with email and password.'
                  : 'Update your password to keep your account secure.'}
              </p>
              <ChangePasswordForm mobile />
            </>
          )}
          {tab === 'billing' && <Billing mobile />}
          {tab === 'publishing' && <Publishing />}
          {tab === 'api-keys' && <ApiKeys />}
          {tab === 'debug' && <DebugTab />}
        </GradientScrollDiv>
      </div>
    );
  }

  return (
    <AppPage header={<BackTo useBrowserHistory>Settings</BackTo>} gradientBackground={true}>
      <div className={css.toggleContainer}>
        <ToggleButtonGroup
          exclusive
          value={tab}
          onChange={(_, value) => {
            if (value) {
              navigate(`/settings/${value}`, { replace: true });
            }
          }}
        >
          {visibleTabs.map((t) => (
            <ToggleButton
              key={t.value}
              value={t.value}
              className={tab === t.value ? css.active : ''}
            >
              {renderTabLabel(t)}
            </ToggleButton>
          ))}
          <span onClick={handleDebugClick}>
            <ToggleButton
              value="debug"
              className={classNames(
                tab === 'debug' && debugStore.respectfulDebugMode && css.active,
                !debugStore.respectfulDebugMode && css.debugButtonWhenNotInDebugMode,
              )}
              disabled={!debugStore.respectfulDebugMode}
            >
              <span className={classNames(!debugStore.respectfulDebugMode && css.debugButtonText)}>
                Debug
              </span>
            </ToggleButton>
          </span>
        </ToggleButtonGroup>
      </div>
      <GradientScrollDiv className={css.settingsContent}>
        {tab === 'account' && (
          <>
            <ChangeNicknameForm />
            <ChangePasswordForm />
          </>
        )}
        {tab === 'billing' && <Billing />}
        {tab === 'publishing' && <Publishing />}
        {tab === 'api-keys' && <ApiKeys />}
        {tab === 'debug' && <DebugTab />}
      </GradientScrollDiv>
    </AppPage>
  );
});
